import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import type { ProjectFeedbackDto } from '@aiai/shared';
import { fetchProjectFeedback, rotateProjectFeedback, setProjectFeedback } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { ConfirmDialog } from './ConfirmDialog';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

const CURL_SNIPPET = `curl -X POST "$AIAI_FEEDBACK_URL" \\
  -H "Authorization: Bearer $AIAI_FEEDBACK_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"body":"The checkout button does nothing","context":{"path":"/checkout","label":"Checkout"}}'`;

const NODE_SNIPPET = `// Server-side only (e.g. your POST /internal/feedback route).
await fetch(process.env.AIAI_FEEDBACK_URL, {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.AIAI_FEEDBACK_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ body: text, context: { path, label } }),
});`;

/** A snippet / token block with a copy button. */
function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto whitespace-pre rounded-md bg-surface-raised px-3 py-2 font-mono text-xs text-ink">
        {value}
      </pre>
    </div>
  );
}

/**
 * Native end-user feedback for a repo (non-prototype) project — the
 * counterpart of PrototypeFeedbackCard. A one-click shortcut over the
 * project's write-only `feedback:write` Integration API token: enabling mints
 * it (shown exactly once), rotate rotates it, disabling revokes it — it is also
 * listed under Integration tokens. The project's own server forwards end-user
 * feedback to `POST /api/integrations/v1/feedback` with it, and each submission
 * lands on this project's board as a proposed `feedback` issue for the PM.
 * Owner-only, matching the API.
 */
export function ProjectFeedbackCard({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  // The plaintext from the enable/rotate response that minted it — never refetchable.
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const query = useQuery({
    queryKey: qk.projectFeedback(projectId),
    queryFn: () => fetchProjectFeedback(projectId),
  });

  const onMinted = (data: ProjectFeedbackDto) => {
    const { token, ...status } = data;
    setFreshToken(token ?? null);
    queryClient.setQueryData(qk.projectFeedback(projectId), status);
  };

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => setProjectFeedback(projectId, enabled),
    onSuccess: onMinted,
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotateProjectFeedback(projectId),
    onSuccess: (data) => {
      onMinted(data);
      setConfirmRotate(false);
    },
  });

  const status = query.data;
  const enabled = status?.feedbackEnabled ?? false;

  return (
    <SectionCard title="End-user feedback">
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">
          Let your app&#x2019;s users send feedback straight to this project&#x2019;s board.
          Each submission becomes a proposed ticket the Product Manager triages. The token
          is a write-only <code>feedback:write</code> Integration API token: it can file
          feedback and read nothing else, and it is also listed under Integration tokens.
        </p>

        {query.isError ? (
          <Callout tone="danger">{errorMessage(query.error, 'Could not load feedback settings')}</Callout>
        ) : (
          <div className="flex items-center gap-3">
            <input
              id="project-feedback-enabled"
              type="checkbox"
              className="checkbox"
              checked={enabled}
              onChange={(e) => toggleMutation.mutate(e.target.checked)}
              disabled={!status || toggleMutation.isPending}
            />
            <label htmlFor="project-feedback-enabled" className="text-sm text-ink-muted">
              Accept end-user feedback from my app&#x2019;s server
            </label>
            {(toggleMutation.isPending || query.isLoading) && <Spinner className="h-3.5 w-3.5" />}
          </div>
        )}

        {enabled && status && (
          <div className="space-y-3 border-t border-line pt-3">
            <Callout tone="danger">
              Keep this token server-side. Never put it in browser code, a frontend bundle or a
              public repo: anyone holding it can file feedback on this project.
            </Callout>

            {freshToken && (
              <div className="card border-signal/40 bg-signal/5 p-3 space-y-2">
                <p className="text-xs font-medium text-ink">
                  Copy the token now: it is shown only once and cannot be retrieved again.
                </p>
                <CopyBlock label="AIAI_FEEDBACK_TOKEN" value={freshToken} />
                <div className="flex justify-end">
                  <button type="button" className="btn-primary btn-sm" onClick={() => setFreshToken(null)}>
                    Done
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-md bg-surface-raised px-3 py-2.5 space-y-1.5">
              <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
                Set on your app&#x2019;s server
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
                <span className="text-ink-faint select-none">AIAI_FEEDBACK_TOKEN</span>
                <span className="text-ink truncate">
                  {status.feedbackTokenPreview ?? <span className="text-amber-500">not available</span>}
                </span>
                <span className="text-ink-faint select-none">AIAI_FEEDBACK_URL</span>
                <span className="text-ink break-all">
                  {status.feedbackUrl ?? <span className="text-amber-500">not configured</span>}
                </span>
              </div>
            </div>

            <p className="text-xs text-ink-muted">
              Ask the Engineer to &quot;add a feedback button&quot; — it is told to build a
              same-origin server route that reads these env vars.
            </p>

            <CopyBlock label="curl" value={CURL_SNIPPET} />
            <CopyBlock label="Node fetch" value={NODE_SNIPPET} />

            <div className="flex justify-end">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setConfirmRotate(true)}
                disabled={rotateMutation.isPending}
              >
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                Rotate token
              </button>
            </div>
          </div>
        )}

        {toggleMutation.error && (
          <Callout tone="danger">{errorMessage(toggleMutation.error, 'Feedback action failed')}</Callout>
        )}
      </div>

      <ConfirmDialog
        open={confirmRotate}
        title="Rotate feedback token"
        message="Mint a new token? The current one stops working immediately — update AIAI_FEEDBACK_TOKEN on your server."
        confirmLabel="Rotate"
        busy={rotateMutation.isPending}
        error={rotateMutation.isError ? errorMessage(rotateMutation.error, 'Could not rotate the token') : null}
        onConfirm={() => rotateMutation.mutate()}
        onClose={() => setConfirmRotate(false)}
      />
    </SectionCard>
  );
}
